/**
 * 63.14 step 2b, the terraces' build (ARCHITECTURE §5.22 "Terracing under pre-generated bases"; DECISIONS 205 (1); the
 * owner on the sheet: "prefer flatter land but terrace when necessary"): the setup pass that lays a terrace under each
 * pre-generated base whose footing is walkable (core/stands.ts footingOf: a building's plinth, the pad's deck) where the
 * natural ground under its footprint rises more than TERRACE.minRisePeople — flat ground keeps the plinth's bedding
 * (67.52) — and no more than TERRACE.maxStepPeople (over that the site should split into pads along the contour: the
 * sites pass's work, later; until then the plinth stands in). The pad's level is the **median** of the natural ground
 * sampled over the footprint, the level that moves the least earth (Σ |level − natural|). Each terrace is a record in the
 * terrain log (TerrainEdit `kind: 'terrace'`, applied through applyTerrainEdit: the record and its apron), written at setup
 * in the structures' order before any battle edit, so a replay and a sector re-fight lay it again (R13). Behind
 * `GameConfig.terraces` (the Lab's Terraces row beside Modular buildings): off, no record, no apron, every byte as before.
 * The seat (buildBaseLift) then finds the pad's level under the whole footprint and the stand's deck is that level.
 */
import type { GameState, PlanetState, TerrainEdit } from '../types';
import type { Terrace } from '../types/ground';
import { TERRACE, terraceCentre, terraceReach } from './terraceGround';
import { applyTerrainEdit } from './terrainEdits';
import { personSurfaceRadius, groundAt, type GroundLevel } from './fineTerrain';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { footingOf } from './stands';
import { structureFrame } from './hits';
import { vecNormalize } from '../math';
import { getSurfaceRadius } from './surface';
import { layoutFor } from './modules';
import { OUTSIDE, roomGraph } from './roomGraph';
import { MODULE_CELL, MODULE_SPAN } from '../constants/modules';
import type { StructureState } from '../types';

/** The log's record of a terrace laid at setup. */
export function terraceEdit(planetIndex: number, planet: PlanetState, t: Terrace): TerrainEdit {
  return { kind: 'terrace', planetIndex, planetId: planet.id, point: terraceCentre(planet, t), radius: terraceReach(t), depth: 0, terrace: t };
}

/**
 * 69.42 (the terraces' planned "vehicle ramp at the gate"): the ramps a terrace `t` takes for the building `s` on it — each of
 * its layout's vehicle gates (`gate`, `gateWide`) onto the outside, on the pad's edge the gate faces (its outward normal in the
 * pad's frame), where the gate stands along that edge, the gate's half opening and TERRACE.rampMarginPeople wide; and as long
 * as the ground straight out from it stands above the ramp's line — to the first quarter person past it where it does not, TERRACE.rampReachBands
 * bands at most — read on the planet with `t` laid (its coarse apron moves the mesh round the pad: before it, the ground behind
 * cv-1's pool's back gate stood under the line and that gate took none) but without `t`'s own rule. None where the ground there
 * never stands above it (a fill bank, the hardstand: no cut to ramp), none for a building with no layout or no gate.
 */
export function gateRamps(planet: PlanetState, t: Terrace, s: Pick<StructureState, 'type' | 'surfaceDir' | 'yaw' | 'builder' | 'stacks'>, config: GameState['config'], ps: ReturnType<typeof personSetOf>): NonNullable<Terrace['ramps']> {
  const layout = layoutFor(s, config);
  if (!layout) return [];
  const person = PERSON_BODY_HEIGHT * ps.k;
  const laid = applyTerrainEdit(planet, { kind: 'terrace', planetIndex: 0, planetId: planet.id, point: terraceCentre(planet, t), radius: terraceReach(t), depth: 0, terrace: t });
  const ground = { ...laid, terraces: (laid.terraces ?? []).filter(x => x.id !== t.id) };   // (its apron, not its rule)
  const up = vecNormalize(s.surfaceDir), { tx, tz } = structureFrame(s as StructureState, up), R = planet.radius;
  const d = t.dir, f = t.facing, rx = f.y * d.z - f.z * d.y, ry = f.z * d.x - f.x * d.z, rz = f.x * d.y - f.y * d.x;   // (the pad's x, as terraceLocal reads it)
  const padDir = (x: number, z: number) => vecNormalize({ x: d.x + (rx * x + f.x * z) / R, y: d.y + (ry * x + f.y * z) / R, z: d.z + (rz * x + f.z * z) / R });
  const out: Array<NonNullable<Terrace['ramps']>[number]> = [];
  for (const w of roomGraph(layout, new Set(), undefined).ways) {
    if ((w.kind !== 'gate' && w.kind !== 'gateWide') || (w.a !== OUTSIDE && w.b !== OUTSIDE)) continue;
    // its spot and its outward normal (from the inside to the outside) in the world, then in the pad's frame
    const sgn = w.b === OUTSIDE ? 1 : -1;
    const at = vecNormalize({ x: up.x + (tx.x * w.x + tz.x * w.z) * person / R, y: up.y + (tx.y * w.x + tz.y * w.z) * person / R, z: up.z + (tx.z * w.x + tz.z * w.z) * person / R });
    const n = { x: sgn * (tx.x * w.nx + tz.x * w.nz), y: sgn * (tx.y * w.nx + tz.y * w.nz), z: sgn * (tx.z * w.nx + tz.z * w.nz) };
    const ox = n.x * rx + n.y * ry + n.z * rz, oz = n.x * f.x + n.y * f.y + n.z * f.z;
    const lx = ((at.x - d.x) * rx + (at.y - d.y) * ry + (at.z - d.z) * rz) * R, lz = ((at.x - d.x) * f.x + (at.y - d.y) * f.y + (at.z - d.z) * f.z) * R;
    const half = ((MODULE_SPAN[w.kind] ?? 1) * MODULE_CELL / 2 + TERRACE.rampMarginPeople) * person;
    const side: 'x+' | 'x-' | 'z+' | 'z-' = Math.abs(oz) >= Math.abs(ox) ? (oz > 0 ? 'z+' : 'z-') : (ox > 0 ? 'x+' : 'x-');
    const along = side[0] === 'z' ? lx : lz;
    // its length: out along the lane's middle, the last point the ground stands above the ramp's line
    const step = 0.25 * person, reach = t.band * TERRACE.rampReachBands;
    let last = -1;
    for (let e = step; e <= reach + 1e-9; e += step) {
      const p = side === 'z+' ? padDir(along, t.halfZ + e) : side === 'z-' ? padDir(along, -t.halfZ - e) : side === 'x+' ? padDir(t.halfX + e, along) : padDir(-t.halfX - e, along);
      if (personSurfaceRadius(ground, p, ps) > t.level + TERRACE.rampBatter * e) last = e;
    }
    if (last < 0) continue;
    out.push({ side, at: along, half, length: Math.min(reach, last + step) });   // (to the first sample at or under the line: where it meets the ground)
  }
  return out;
}

/** The least natural rise under a footing worth a terrace (people): the Lab's dial (GameConfig.terraceRisePeople), else TERRACE.minRisePeople. */
export function terraceMinRise(config: Pick<GameState['config'], 'terraceRisePeople'>): number {
  return config.terraceRisePeople ?? TERRACE.minRisePeople;
}

/** Whether `config` lays terraces (the switch, with the person's fine ground to lay them in). */
export function terracesOn(state: Pick<GameState, 'config'>): boolean {
  return !!state.config.terraces && personSetOf(state).fineAmplitude > 0;
}

/**
 * The natural ground under a footprint, sampled TERRACE.samples a side over the pad (people → world), as the fine ground
 * has it (the mesh, the relief, the digs, any terrace laid before): its rise (max − min) and its median — the level.
 */
export function footprintGround(planet: PlanetState, dir: { x: number; y: number; z: number }, facing: { x: number; y: number; z: number }, across: { x: number; y: number; z: number }, halfX: number, halfZ: number, ps: ReturnType<typeof personSetOf>, at: GroundLevel = 'full'): { rise: number; level: number; samples: number[] } {
  const n = TERRACE.samples, R = planet.radius, samples: number[] = [];
  for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) {
    const x = (-1 + (2 * i) / (n - 1)) * halfX, z = (-1 + (2 * j) / (n - 1)) * halfZ;
    const d = vecNormalize({ x: dir.x + (across.x * x + facing.x * z) / R, y: dir.y + (across.y * x + facing.y * z) / R, z: dir.z + (across.z * x + facing.z * z) / R });
    samples.push(groundAt(planet, d, at, ps));
  }
  const sorted = samples.slice().sort((a, b) => a - b);
  const m = sorted.length >> 1, level = sorted.length % 2 ? sorted[m] : (sorted[m - 1] + sorted[m]) / 2;
  return { rise: sorted[sorted.length - 1] - sorted[0], level, samples };
}

/**
 * The setup pass: every standing structure with a walkable footing, in order, gets a terrace where its footprint's rise
 * asks for one; each is laid on its planet through the log (the state's `terrainEdits` grows by the record). The same
 * state when none is laid, or the switch is off.
 */
export function terraceBuilds(state: GameState): GameState {
  if (!terracesOn(state)) return state;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  let planets = state.planets, edits = state.terrainEdits ?? [], laid = false;
  let nextId = 1;
  for (const p of planets) for (const t of p.terraces ?? []) nextId = Math.max(nextId, t.id + 1);
  for (const s of state.structures) {
    if (s.health <= 0) continue;
    const foot = footingOf(s, state.config);
    if (!foot) continue;
    const planet = planets[s.planetIndex];
    if (!planet || planet.type !== 'planet' || !planet.alive) continue;
    const up = vecNormalize(s.surfaceDir), { tz } = structureFrame(s, up);
    // (the pad's x axis as terraceLocal reads it: facing × up — the structure's own tx up to sign, which a symmetric footprint does not mind)
    const across = { x: tz.y * up.z - tz.z * up.y, y: tz.z * up.x - tz.x * up.z, z: tz.x * up.y - tz.y * up.x };
    const halfX = foot.halfXPeople * person, halfZ = foot.halfZPeople * person;
    const { rise, level } = footprintGround(planet, up, tz, across, halfX, halfZ, ps);
    if (rise < terraceMinRise(state.config) * person || rise > TERRACE.maxStepPeople * person) continue;
    const t0: Terrace = { id: nextId++, dir: up, facing: tz, halfX, halfZ, band: TERRACE.bandPeople * person, level };
    const ramps = gateRamps(planet, t0, s, state.config, ps), t: Terrace = ramps.length ? { ...t0, ramps } : t0;   // (69.42: its gates' ramps)
    const e = terraceEdit(s.planetIndex, planet, t);
    planets = planets.map((p, i) => (i === s.planetIndex ? applyTerrainEdit(p, e) : p));
    edits = [...edits, e];
    laid = true;
  }
  return laid ? { ...state, planets, terrainEdits: edits } : state;
}

/**
 * A readout for the page's console (main.ts at a match's start, with the switch): the structures terraced, each with
 * the step it made (the level over the coarse ground at its centre, people), and how many footings were read in all — or
 * why none was laid (the switch off; no footing's rise in the window: flat sites keep their bedding, over the cap the
 * plinth stands in). Presentation only.
 */
export function terraceReadout(state: GameState): string {
  if (!terracesOn(state)) return 'off (the Terraces row, or no fine ground)';
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const laid = (state.terrainEdits ?? []).filter(e => e.kind === 'terrace' && e.terrace);
  const footings = state.structures.filter(s => s.health > 0 && footingOf(s, state.config)).length;
  if (!laid.length) return `none laid under ${footings} footing(s): every site's natural rise under ${terraceMinRise(state.config)} people (the plinth's bedding takes it; the Terrace from row lowers it) or over the ${TERRACE.maxStepPeople}-people step cap`;
  const parts = laid.map(e => {
    const t = e.terrace!, s = state.structures.find(x => Math.abs(x.surfaceDir.x - t.dir.x) + Math.abs(x.surfaceDir.y - t.dir.y) + Math.abs(x.surfaceDir.z - t.dir.z) < 1e-9);
    const planet = state.planets[e.planetIndex];
    const step = planet ? (t.level - getSurfaceRadius(planet, t.dir)) / person : 0;
    return `${s ? `${s.type}#${s.id}` : '?'} (${step >= 0 ? '+' : ''}${step.toFixed(2)} people over the mesh)`;
  });
  return `${laid.length} of ${footings} footing(s): ${parts.join(', ')}`;
}

